import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  
  Popover,
  Stack,
  SelectChangeEvent,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Translate as TranslateIcon,
  ExpandMore as ExpandMoreIcon,
  VolumeUp as VolumeUpIcon,
  Stop as StopIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { useTranslateStreamMutation } from '@/features/translation/hooks/useTranslateStreamMutation';
import { responsivePagePaddingX } from '@/utils/responsiveLayout';

const LANGUAGES = [
  { code: 'auto', name: 'Auto detect' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'cs', name: 'Czech' },
  { code: 'pl', name: 'Polish' },
  { code: 'da', name: 'Danish' },
  { code: 'it', name: 'Italian' },
  { code: 'de', name: 'German' },
  { code: 'nl', name: 'Dutch' },
  { code: 'sv', name: 'Swedish' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'zh-Hans', name: 'Chinese (Simplified)' },
  { code: 'zh-Hant', name: 'Chinese (Traditional)' },
  { code: 'ko', name: 'Korean' },
];

// const PROFANITY_ACTIONS = [
//   { value: 'none', label: 'No action' },
//   { value: 'mask', label: 'Mask' },
//   { value: 'block', label: 'Block' },
// ];

// const TEXT_TYPES = [
//   { value: 'text', label: 'Text' },
//   { value: 'html', label: 'HTML' },
// ];

const MAX_CHARS = 20000;
// Pricing: Azure Translator (Switzerland North) list price is €8.553 per 1,000,000 characters.
// Convert to EUR per 1,000 characters: 8.553 / 1_000_000 * 1000 = 0.008553 EUR per 1000 chars.
// Update this constant if you want to use a different rate or region.
// RH
// https://azure.microsoft.com/en-us/pricing/details/cognitive-services/translator/
const TRANSLATION_PRICE_PER_1000_CHARS = 0.008553;

const TranslateTextPage: React.FC = () => {
  const [fromLang, setFromLang] = useState<string>('auto');
  const [toLang, setToLang] = useState<string>('en');
  const [openAdvanced, setOpenAdvanced] = useState<boolean>(false);
  // const [profanityAction, setProfanityAction] = useState<string>('none');
  // const [textType, setTextType] = useState<string>('text');
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [preferredSourceVoiceURI, setPreferredSourceVoiceURI] = useState<string>('Default browser voice');
  const [preferredTranslationVoiceURI, setPreferredTranslationVoiceURI] = useState<string>('Auto select');

  const [openSourcePopover, setOpenSourcePopover] = useState<boolean>(false);
  const [openTranslationPopover, setOpenTranslationPopover] = useState<boolean>(false);
  const sourceIconRef = useRef<HTMLButtonElement | null>(null);
  const translationIconRef = useRef<HTMLButtonElement | null>(null);

  const [originalText, setOriginalText] = useState<string>('');
  const [translation, setTranslation] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSpeakingOriginal, setIsSpeakingOriginal] = useState<boolean>(false);
  const [isSpeakingTranslation, setIsSpeakingTranslation] = useState<boolean>(false);
  // Keep a reference to the active utterances so stop is specific
  const activeOriginalUtterRef = useRef<SpeechSynthesisUtterance | null>(null);
  const activeTranslationUtterRef = useRef<SpeechSynthesisUtterance | null>(null);
  
  // Voice management for TTS
  const voicesRef = useRef<SpeechSynthesisVoice[] | null>(null);
  const selectedVoiceRef = useRef<SpeechSynthesisVoice | null>(null);

  const translationBoxRef = useRef<HTMLTextAreaElement | null>(null);

  // Translation streaming mutation hook
  const translateMutation = useTranslateStreamMutation((chunk: string) => {
    setTranslation(prev => {
      if (!prev) return chunk;
      // If chunk is only whitespace/newlines, append directly
      if (!chunk.trim()) return prev + chunk;
      // If either side already ends/starts with a newline, append directly
      const prevEndsWithNewline = /\n$/.test(prev);
      const chunkStartsWithNewline = /^\n/.test(chunk);
      if (prevEndsWithNewline || chunkStartsWithNewline) return prev + chunk;
      // If chunk contains any internal newline, append directly to preserve paragraphs
      if (chunk.includes('\n')) return prev + chunk;
      // Otherwise join with a single space
      return prev + ' ' + chunk;
    });
  });

  // Auto-scroll translation box as content arrives
  useEffect(() => {
    if (translationBoxRef.current && translation) {
      try {
        translationBoxRef.current.scrollTop = translationBoxRef.current.scrollHeight;
      } catch (e) {
        // ignore if element not scrollable
      }
    }
  }, [translation]);

  const handleFromChange = (e: SelectChangeEvent<string>) => setFromLang(e.target.value as string);
  const handleToChange = (e: SelectChangeEvent<string>) => setToLang(e.target.value as string);

  // Load available voices and choose a voice matching the target language
  const loadVoices = () => {
    try {
      const vs = window.speechSynthesis.getVoices() || [];
      voicesRef.current = vs;
      // Filter voices to only those matching supported LANGUAGES codes (primary subtag)
      const supportedCodes = new Set(
        LANGUAGES
          .map(l => l.code.toLowerCase().split('-')[0])
          .filter(c => c !== 'auto')
      );
      const filtered = vs.filter(v => {
        if (!v.lang) return false;
        const primary = v.lang.toLowerCase().split('-')[0];
        return supportedCodes.has(primary);
      });
      setAvailableVoices(filtered);
      // restore persisted preferences if present
      try {
        const s = localStorage.getItem('ttsPreferredSourceVoiceURI');
        if (s) setPreferredSourceVoiceURI(s);
        const t = localStorage.getItem('ttsPreferredTranslationVoiceURI');
        if (t) setPreferredTranslationVoiceURI(t);
      } catch (e) {
        // ignore localStorage errors
      }
      // try to select a voice for current toLang
      selectVoiceForLang(toLang);
    } catch (e) {
      voicesRef.current = [];
    }
  };

  // Return best matching voice for a language code (e.g., 'en', 'fr')
  const getVoiceForLang = (langCode: string | null): SpeechSynthesisVoice | null => {
    if (!langCode || !voicesRef.current || voicesRef.current.length === 0) return null;
    const lc = langCode.toLowerCase();
    const primaryCode = lc.split('-')[0];
    // Special-case: prefer Spanish (Spain) voices when requesting 'es'
    if (primaryCode === 'es') {
      // prefer explicit es-ES
      const esEs = voicesRef.current.find(v => v.lang && v.lang.toLowerCase().startsWith('es-es'));
      if (esEs) return esEs;
      // prefer names that mention 'castilian' or 'spain'
      const esName = voicesRef.current.find(v => v.name && /castilian|spain|spanish \(spain\)/i.test(v.name));
      if (esName) return esName;
    }
    // Exact prefix match (en -> en-US)
    let v = voicesRef.current.find(v => v.lang && v.lang.toLowerCase().startsWith(lc));
    if (v) return v;
    // Match primary subtag (pt-BR -> pt)
    v = voicesRef.current.find(v => {
      if (!v.lang) return false;
      const parts = v.lang.toLowerCase().split('-');
      return parts[0] === primaryCode;
    });
    if (v) return v;
    // Name contains language code heuristic
    v = voicesRef.current.find(v => v.name && v.name.toLowerCase().includes(primaryCode));
    if (v) return v;
    return null;
  };

  const selectVoiceForLang = (langCode: string) => {
    if (!voicesRef.current || voicesRef.current.length === 0) return;
    const lc = langCode.toLowerCase();
    const primaryCode = lc.split('-')[0];
    // Special-case: when requesting Spanish, prefer Spain variant (es-ES) if available
    if (primaryCode === 'es') {
      const esEs = voicesRef.current.find(v => v.lang && v.lang.toLowerCase().startsWith('es-es'));
      if (esEs) {
        selectedVoiceRef.current = esEs;
        return;
      }
      const esName = voicesRef.current.find(v => v.name && /castilian|spain|spanish \(spain\)/i.test(v.name));
      if (esName) {
        selectedVoiceRef.current = esName;
        return;
      }
    }
    // prefer exact prefix matches (e.g., 'en' -> 'en-US' or 'en-GB')
    const pref = voicesRef.current.find(v => v.lang && v.lang.toLowerCase().startsWith(langCode.toLowerCase()));
    if (pref) {
      selectedVoiceRef.current = pref;
      return;
    }
    // Some voices use long codes like 'pt-BR' while langCode might be 'pt-br' or 'pt'
    const alt = voicesRef.current.find(v => {
      if (!v.lang) return false;
      const parts = v.lang.toLowerCase().split('-');
      return parts[0] === primaryCode;
    });
    if (alt) {
      selectedVoiceRef.current = alt;
      return;
    }
    // Fallback: choose a voice whose name contains the language code
    const byName = voicesRef.current.find(v => v.name && v.name.toLowerCase().includes(primaryCode));
    if (byName) {
      selectedVoiceRef.current = byName;
      return;
    }
    // otherwise leave as null
    selectedVoiceRef.current = null;
  };

  // Initialize voices once and on changes
  useEffect(() => {
    loadVoices();
    const handler = () => loadVoices();
    window.speechSynthesis.addEventListener?.('voiceschanged', handler);
    return () => window.speechSynthesis.removeEventListener?.('voiceschanged', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-select voice when toLang changes
  useEffect(() => {
    selectVoiceForLang(toLang);
  }, [toLang]);

  // preferred voice persistence handled per-control; no global selectedVoiceRef effect here

  // Helpers to filter available voices according to a language code (primary subtag)
  const voicesForLang = (langCode: string) => {
    if (!voicesRef.current) return [] as SpeechSynthesisVoice[];
    const lc = langCode.toLowerCase().split('-')[0];
    return voicesRef.current.filter(v => {
      if (!v.lang) return false;
      const primary = v.lang.toLowerCase().split('-')[0];
      return primary === lc;
    });
  };

  // Return the effective voice that will be used for original/translation
  const getEffectiveVoice = (isOriginal: boolean): SpeechSynthesisVoice | null => {
    const langCode = isOriginal ? (fromLang === 'auto' ? null : fromLang) : toLang;
    const pref = isOriginal ? preferredSourceVoiceURI : preferredTranslationVoiceURI;
    let applied: SpeechSynthesisVoice | null = null;
    if (pref && ((isOriginal && pref !== 'Default browser voice') || (!isOriginal && pref !== 'Auto select')) && voicesRef.current) {
      applied = voicesRef.current.find(v => v.voiceURI === pref || v.name === pref || v.lang === pref) || null;
    }
    // Important: selectedVoiceRef is populated based on the current `toLang`.
    // To avoid swapping voices (i.e. playing the translation voice when speaking the source),
    // only consult selectedVoiceRef for translation playback. For source playback, prefer
    // language-based heuristics (getVoiceForLang) when no explicit preferred voice was set.
    if (!applied) {
      if (!isOriginal && selectedVoiceRef.current) {
        applied = selectedVoiceRef.current;
      }
    }
    if (!applied && langCode) applied = getVoiceForLang(langCode);
    return applied;
  };

  const handleTranslate = async () => {
    if (!originalText.trim()) return;
    setTranslation('');
    setError('');
    
    try {
      await translateMutation.mutateAsync({
        text: originalText.slice(0, MAX_CHARS),
        to: toLang,
        from_lang: fromLang,
      });
    } catch (err: any) {
      if (err.name === 'AbortError') {
        setTranslation(prev => prev ? prev : '[Streaming cancelled]');
      } else {
        setError(err instanceof Error ? err.message : `Translation error: ${String(err)}`);
      }
    }
  };

  const handleClear = () => {
    setOriginalText('');
    setTranslation('');
    setError('');
  };

  const handlePlay = () => {
    // Placeholder for TTS playback
    // Could integrate speechSynthesis here later
    if (!originalText) return;
    try {
  const utter = new SpeechSynthesisUtterance(originalText);
  // store as active utterance
  activeOriginalUtterRef.current = utter;
      // Determine effective voice for the original text (respects Source voice)
      const appliedVoice = getEffectiveVoice(true);
      if (appliedVoice) {
        // set lang first (some browsers use lang to choose voice behavior)
        if (appliedVoice.lang) utter.lang = appliedVoice.lang;
        try { utter.voice = appliedVoice; } catch (e) { /* ignore if assignment fails */ }
      } else if (fromLang && fromLang !== 'auto') {
        // fallback to setting lang to fromLang
        utter.lang = fromLang;
      }

      // stop any existing speech and start new
      // ensure existing speech is stopped and then speak
      try { window.speechSynthesis.cancel(); } catch (e) { /* ignore */ }
      window.speechSynthesis.speak(utter);
      setIsSpeakingOriginal(true);
      utter.onend = () => {
        // only clear if this is still the active utterance
        if (activeOriginalUtterRef.current === utter) activeOriginalUtterRef.current = null;
        setIsSpeakingOriginal(false);
      };
      utter.onerror = () => {
        if (activeOriginalUtterRef.current === utter) activeOriginalUtterRef.current = null;
        setIsSpeakingOriginal(false);
      };
    } catch (e) {
      console.warn('TTS not available', e);
    }
  };

  const handleStopOriginal = () => {
    try {
      // cancel only if there is an active utterance
      if (activeOriginalUtterRef.current) {
        // Cancel all speech (no browser API to cancel a specific utterance),
        // but we can clear our active ref so state won't be cleared incorrectly
        activeOriginalUtterRef.current = null;
        window.speechSynthesis.cancel();
      }
    } catch (e) {
      console.warn('Stop TTS failed', e);
    }
    setIsSpeakingOriginal(false);
  };

  const handleCopyTranslation = async () => {
    if (!translation) return;
    try {
      await navigator.clipboard.writeText(translation);
    } catch (e) {
      console.warn('Copy to clipboard failed', e);
    }
  };

  const handlePlayTranslation = () => {
    if (!translation) return;
    try {
  const utter = new SpeechSynthesisUtterance(translation);
  activeTranslationUtterRef.current = utter;
      // Determine effective voice for the translation (respects Translation voice)
      const appliedVoiceT = getEffectiveVoice(false);
      if (appliedVoiceT) {
        if (appliedVoiceT.lang) utter.lang = appliedVoiceT.lang;
        try { utter.voice = appliedVoiceT; } catch (e) { /* ignore */ }
      } else if (toLang) {
        utter.lang = toLang;
      }

      try { window.speechSynthesis.cancel(); } catch (e) { /* ignore */ }
      window.speechSynthesis.speak(utter);
      setIsSpeakingTranslation(true);
      utter.onend = () => {
        if (activeTranslationUtterRef.current === utter) activeTranslationUtterRef.current = null;
        setIsSpeakingTranslation(false);
      };
      utter.onerror = () => {
        if (activeTranslationUtterRef.current === utter) activeTranslationUtterRef.current = null;
        setIsSpeakingTranslation(false);
      };
    } catch (e) {
      console.warn('TTS not available', e);
    }
  };

  const handleStopTranslation = () => {
    try {
      if (activeTranslationUtterRef.current) {
        activeTranslationUtterRef.current = null;
        window.speechSynthesis.cancel();
      }
    } catch (e) {
      console.warn('Stop TTS failed', e);
    }
    setIsSpeakingTranslation(false);
  };

  const charsLeft = MAX_CHARS - originalText.length;
  const isTranslateDisabled = originalText.trim().length === 0 || originalText.length > MAX_CHARS;
  const priceFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' });
  const translationPrice = translation ? (translation.length / 1000) * TRANSLATION_PRICE_PER_1000_CHARS : 0;

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        px: responsivePagePaddingX,
      }}
    >
      {/* Page Header */}
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1,
          width: '100%',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : 'background.default',
          position: 'sticky',
          top: 0,
          zIndex: 10
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ fontSize: '24px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>🔠</Box>
          <Typography variant="subtitle1" fontWeight={600} color="primary.main">
            Translate Text
          </Typography>
        </Box>
      </Box>
      {/* Popovers for mobile tap behavior */}
      <Popover
        open={openSourcePopover}
        anchorEl={sourceIconRef.current}
        onClose={() => setOpenSourcePopover(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 300 }}>
          <Typography variant="body2">
            "Default browser voice" chooses the language and voice set by default in your browser. Select a specific voice to change it.
          </Typography>
        </Box>
      </Popover>

      <Popover
        open={openTranslationPopover}
        anchorEl={translationIconRef.current}
        onClose={() => setOpenTranslationPopover(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 300 }}>
          <Typography variant="body2">
            'Auto select' chooses a voice that best matches the translation language (e.g. Spanish → Spanish (Spain) when available). Select a specific voice to override.
          </Typography>
        </Box>
      </Popover>

      <Box sx={{ py: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {/* Header: Configure - Horizontal Layout (no container) */}
        <Box sx={{ mt: '10px', mb: '10px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <FormControl
            sx={{
              width: { xs: '100%', sm: 'calc(50% - 6px)', md: 'auto' },
              minWidth: { md: 220 },
            }}
          >
            <InputLabel id="from-label" sx={{ fontSize: '0.875rem' }}>Translate from</InputLabel>
            <Select
              labelId="from-label"
              value={fromLang}
              label="Translate from"
              onChange={handleFromChange}
              size="small"
              sx={{ fontSize: '0.875rem' }}
            >
              {LANGUAGES.map(l => (
                <MenuItem key={l.code} value={l.code}>{l.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            sx={{
              width: { xs: '100%', sm: 'calc(50% - 6px)', md: 'auto' },
              minWidth: { md: 220 },
            }}
          >
            <InputLabel id="to-label" sx={{ fontSize: '0.875rem' }}>Translate to</InputLabel>
            <Select
              labelId="to-label"
              value={toLang}
              label="Translate to"
              onChange={handleToChange}
              size="small"
              sx={{ fontSize: '0.875rem' }}
            >
              {LANGUAGES.filter(l => l.code !== 'auto').map(l => (
                <MenuItem key={l.code} value={l.code}>{l.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            endIcon={<ExpandMoreIcon sx={{ transform: openAdvanced ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
            onClick={() => setOpenAdvanced(open => !open)}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'space-between', sm: 'center' },
              color: 'text.primary',
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            Advanced settings
          </Button>
        </Box>

  <Collapse in={openAdvanced}>
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            {/* <FormControl sx={{ mt: 1.5, minWidth: { xs: 170, sm: 220 } }}>
              <InputLabel id="profanity-label" sx={{ fontSize: '0.875rem' }}>Profanity filter</InputLabel>
              <Select
                labelId="profanity-label"
                value={profanityAction}
                label="Profanity filter"
                onChange={(e) => setProfanityAction(e.target.value as string)}
                size="small"
                sx={{ fontSize: '0.875rem' }}
              >
                {PROFANITY_ACTIONS.map(a => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ mt: 1.5, minWidth: { xs: 170, sm: 220 } }}>
              <InputLabel id="texttype-label" sx={{ fontSize: '0.875rem' }}>Text type</InputLabel>
              <Select
                labelId="texttype-label"
                value={textType}
                label="Text type"
                onChange={(e) => setTextType(e.target.value as string)}
                size="small"
                sx={{ fontSize: '0.875rem' }}
              >
                {TEXT_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl> */}


            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <FormControl
                sx={{
                  width: { xs: '100%', sm: '100%', md: 455 },
                  maxWidth: { xs: '100%', md: 520 },
                }}
              >
                <InputLabel id="preferred-source-voice-label" sx={{ fontSize: '0.875rem' }}>Source voice</InputLabel>
                <Select
                  labelId="preferred-source-voice-label"
                  value={preferredSourceVoiceURI}
                  label="Source voice"
                  size="small"
                  onChange={(e) => {
                    const val = e.target.value as string;
                    setPreferredSourceVoiceURI(val);
                    try {
                      if (val === 'Default browser voice') localStorage.removeItem('ttsPreferredSourceVoiceURI');
                      else localStorage.setItem('ttsPreferredSourceVoiceURI', val);
                    } catch (err) { }
                    if (val === 'Default browser voice') selectedVoiceRef.current = null;
                  }}
                  sx={{ fontSize: '0.875rem' }}
                >
                    <MenuItem value="Default browser voice">Default browser voice</MenuItem>
                  {(fromLang === 'auto' ? availableVoices : (voicesForLang(fromLang).length ? voicesForLang(fromLang) : availableVoices)).map(v => (
                    <MenuItem key={v.voiceURI || v.name} value={v.voiceURI || v.name}>
                      {v.name} {v.lang ? `(${v.lang})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                aria-label="source-voice-info"
                size="small"
                onClick={() => setOpenSourcePopover(v => !v)}
                ref={(el) => { sourceIconRef.current = el }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <FormControl
                sx={{
                  width: { xs: '100%', sm: '100%', md: 455 },
                  maxWidth: { xs: '100%', md: 520 },
                }}
              >
                <InputLabel id="preferred-translation-voice-label" sx={{ fontSize: '0.875rem' }}>Translation voice</InputLabel>
                <Select
                  labelId="preferred-translation-voice-label"
                  value={preferredTranslationVoiceURI}
                  label="Translation voice"
                  size="small"
                  onChange={(e) => {
                    const val = e.target.value as string;
                    setPreferredTranslationVoiceURI(val);
                    try {
                      if (val === 'Auto select') localStorage.removeItem('ttsPreferredTranslationVoiceURI');
                      else localStorage.setItem('ttsPreferredTranslationVoiceURI', val);
                    } catch (err) { }
                  }}
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="Auto select">Auto select</MenuItem>
                  {(voicesForLang(toLang).length ? voicesForLang(toLang) : availableVoices).map(v => (
                    <MenuItem key={v.voiceURI || v.name} value={v.voiceURI || v.name}>
                      {v.name} {v.lang ? `(${v.lang})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                aria-label="translation-voice-info"
                size="small"
                onClick={() => setOpenTranslationPopover(v => !v)}
                ref={(el) => { translationIconRef.current = el }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Collapse>
        </Box>

      {/* Original + Translation below */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '10px', flex: 1, minHeight: 0, alignItems: 'stretch' }}>
        <Box sx={{ flex: 1, flexShrink: 1, flexBasis: 0, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <TextField
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              placeholder="Enter source text to translate..."
              multiline
              variant="outlined"
              fullWidth
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              helperText={`${originalText.length} / ${MAX_CHARS} Characters`}
              FormHelperTextProps={{ sx: { textAlign: 'left' } }}
              error={originalText.length > MAX_CHARS}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'flex-start'
                },
                '& .MuiInputBase-input': {
                  overflow: 'auto !important',
                  height: '100% !important'
                }
              }}
            />            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Clear source text" arrow>
                  <IconButton aria-label="clear" onClick={handleClear} size="large">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isSpeakingOriginal ? "Stop" : "Listen"} arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton aria-label="tts" onClick={isSpeakingOriginal ? handleStopOriginal : handlePlay} size="large">
                      {isSpeakingOriginal ? <StopIcon /> : <VolumeUpIcon />}
                    </IconButton>
                    {isSpeakingOriginal && (
                      <CircularProgress size={18} thickness={5} sx={{ ml: 0.5 }} />
                    )}
                  </Box>
                </Tooltip>
              </Stack>

                {/* Voice name caption removed for a cleaner UI */}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="caption"
                  color={charsLeft < 0 ? 'error' : 'text.secondary'}
                  sx={{
                    // Ensure subtle but readable color in both themes
                    fontWeight: 500,
                    minWidth: 64,
                    textAlign: 'right'
                  }}
                >
                  {charsLeft} remaining
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<TranslateIcon sx={{ color: 'inherit' }} />}
                  onClick={handleTranslate}
                  disabled={isTranslateDisabled || translateMutation.isPending}
                  sx={(theme) => ({
                    // Enabled state: use primary background and contrast text for clear visual weight
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    // Disabled state: use action disabled tokens
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                    textTransform: 'none'
                  })}
                >
                  {translateMutation.isPending ? 'Translating…' : 'Translate'}
                </Button>
              </Box>
            </Box>
        </Box>

        <Box sx={{ flex: 1, flexShrink: 1, flexBasis: 0, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }} action={
                <Button size="small" startIcon={<RefreshIcon />} onClick={handleTranslate}>
                  Retry
                </Button>
              }>
                {error}
              </Alert>
            )}

            <TextField
              value={translation || (translateMutation.isPending ? 'Translating...' : '')}
              placeholder="Your translation will appear here."
              multiline
              variant="outlined"
              fullWidth
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              InputProps={{
                readOnly: true,
              }}
              inputRef={(el) => { translationBoxRef.current = el as HTMLTextAreaElement | null; }}
              sx={(theme) => ({
                flex: 1,
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'flex-start',
                  backgroundColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : theme.palette.background.paper
                },
                '& .MuiInputBase-input': {
                  overflow: 'auto !important',
                  height: '100% !important'
                }
              })}
            />

            {/* Estimated translation price - keep vertical space even when not shown */}
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                minHeight: '1.66em',
                color: 'text.secondary',
                paddingLeft: '10px',
                visibility: translation && !translateMutation.isPending ? 'visible' : 'hidden'
              }}
            >
              {translation && !translateMutation.isPending ? `Estimated price: ${priceFormatter.format(translationPrice)}` : '\u00A0'}
            </Typography>

            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Copy translation" arrow>
                  <IconButton 
                    aria-label="copy translation" 
                    onClick={handleCopyTranslation} 
                    size="large"
                    disabled={!translation}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isSpeakingTranslation ? 'Stop' : 'Listen to translation'} arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton 
                      aria-label={isSpeakingTranslation ? 'stop translation tts' : 'listen to translation'} 
                      onClick={isSpeakingTranslation ? handleStopTranslation : handlePlayTranslation} 
                      size="large"
                      disabled={!translation}
                    >
                      {isSpeakingTranslation ? <StopIcon /> : <VolumeUpIcon />}
                    </IconButton>
                    {isSpeakingTranslation && (
                      <CircularProgress size={18} thickness={5} sx={{ ml: 0.5 }} />
                    )}
                  </Box>
                </Tooltip>
              </Stack>

                {/* Voice name caption removed for a cleaner UI */}
            </Box>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default TranslateTextPage;
